import React from "react";
import { Header } from "./Header";
import { Footer } from "./Footer";

export default function PrivacyPolicy() {
  return (
    <div className="min-h-screen bg-white dark:bg-slate-950 transition-colors duration-200 flex flex-col">
      <Header />

      <main className="flex-1 pt-32 pb-16 px-4">
        <div className="max-w-4xl mx-auto">
          <h1 className="text-4xl font-extrabold text-slate-900 dark:text-white mb-2">
            Privacy Notice
          </h1>
          <p className="text-slate-500 dark:text-slate-400 mb-12">
            Last updated February 07, 2026
          </p>

          <div className="prose prose-slate dark:prose-invert max-w-none space-y-12 text-slate-600 dark:text-slate-300">
            <section>
              <p className="text-lg">
                This privacy notice tells you what to expect us to do with your
                personal information.
              </p>
            </section>

            <section>
              <h2 className="text-2xl font-bold text-slate-900 dark:text-white mb-4">
                Contact details
              </h2>
              <div className="mt-4 p-6 bg-slate-50 dark:bg-slate-900 rounded-xl border border-slate-100 dark:border-slate-800">
                <p className="font-bold text-slate-900 dark:text-white">
                  Taskplan
                </p>
                <p>
                  Email:{" "}
                  <a
                    href="mailto:support@taskplan.run"
                    className="text-indigo-600 dark:text-indigo-400 hover:underline"
                  >
                    support@taskplan.run
                  </a>
                </p>
              </div>
            </section>

            <section>
              <h2 className="text-2xl font-bold text-slate-900 dark:text-white mb-4">
                What information we collect, use, and why
              </h2>
              <p>
                We collect or use the following information to provide and
                improve products and services for clients:
              </p>
              <ul className="list-disc pl-6 space-y-2">
                <li>Names and contact details</li>
              </ul>
            </section>

            <section>
              <h2 className="text-2xl font-bold text-slate-900 dark:text-white mb-4">
                Lawful bases and data protection rights
              </h2>
              <div className="space-y-4">
                <p>
                  Under UK data protection law, we must have a "lawful basis"
                  for collecting and using your personal information. There is a
                  list of possible lawful bases in the UK GDPR. You can find out
                  more about lawful bases on the ICO's website.
                </p>
                <p>
                  Which lawful basis we rely on may affect your data protection
                  rights which are set out in brief below.
                </p>
                <ul className="space-y-4">
                  <li>
                    <strong>Your right of access</strong> - You have the right
                    to ask us for copies of your personal information.
                  </li>
                  <li>
                    <strong>Your right to rectification</strong> - You have the
                    right to ask us to correct or delete personal information
                    you think is inaccurate or incomplete.
                  </li>
                  <li>
                    <strong>Your right to erasure</strong> - You have the right
                    to ask us to delete your personal information.
                  </li>
                  <li>
                    <strong>Your right to restriction of processing</strong> -
                    You have the right to ask us to limit how we can use your
                    personal information.
                  </li>
                  <li>
                    <strong>Your right to object to processing</strong> - You
                    have the right to object to the processing of your personal
                    data.
                  </li>
                  <li>
                    <strong>Your right to data portability</strong> - You have
                    the right to ask that we transfer the personal information
                    you gave us to another organisation, or to you.
                  </li>
                  <li>
                    <strong>Your right to withdraw consent</strong> – When we
                    use consent as our lawful basis you have the right to
                    withdraw your consent at any time.
                  </li>
                </ul>
                <p>
                  To make a data protection rights request, please contact us
                  using the contact details at the top of this privacy notice.
                </p>
              </div>
            </section>

            <section>
              <h2 className="text-2xl font-bold text-slate-900 dark:text-white mb-4">
                Our lawful bases for the collection and use of your data
              </h2>
              <div className="space-y-4">
                <ul className="space-y-4">
                  <li>
                    <strong>Contract</strong> – we have to collect or use the
                    information so we can enter into or carry out a contract
                    with you.
                  </li>
                  <li>
                    <strong>Legitimate interests</strong> – we're collecting or
                    using your information because it benefits you, our
                    organisation or someone else, without causing an undue risk
                    of harm to anyone. Our legitimate interest is to ensure the
                    security, stability, and operational integrity of our
                    service.
                  </li>
                </ul>
              </div>
            </section>

            <section>
              <h2 className="text-2xl font-bold text-slate-900 dark:text-white mb-4">
                Where we get personal information from
              </h2>
              <ul className="list-disc pl-6">
                <li>Directly from you</li>
              </ul>
            </section>

            <section>
              <h2 className="text-2xl font-bold text-slate-900 dark:text-white mb-4">
                How long we keep information
              </h2>
              <p>
                Account information will be held until deletion of your account.
              </p>
            </section>

            <section>
              <h2 className="text-2xl font-bold text-slate-900 dark:text-white mb-4">
                Sharing information outside the UK
              </h2>
              <div className="space-y-4">
                <p>
                  Where necessary, we may transfer personal information outside
                  of the UK. When doing so, we comply with the UK GDPR, making
                  sure appropriate safeguards are in place.
                </p>
                <div className="overflow-x-auto">
                  <table className="min-w-full divide-y divide-slate-200 dark:divide-slate-800 border border-slate-200 dark:border-slate-800 rounded-lg">
                    <tbody className="divide-y divide-slate-200 dark:divide-slate-800">
                      <tr>
                        <td className="px-4 py-2 font-bold bg-slate-50 dark:bg-slate-900/50">
                          Organisation
                        </td>
                        <td className="px-4 py-2">Cloudflare</td>
                      </tr>
                      <tr>
                        <td className="px-4 py-2 font-bold bg-slate-50 dark:bg-slate-900/50">
                          Category
                        </td>
                        <td className="px-4 py-2">Hosting provider</td>
                      </tr>
                      <tr>
                        <td className="px-4 py-2 font-bold bg-slate-50 dark:bg-slate-900/50">
                          Destination
                        </td>
                        <td className="px-4 py-2">
                          EU (Storage), US (Metadata/Logs)
                        </td>
                      </tr>
                      <tr>
                        <td className="px-4 py-2 font-bold bg-slate-50 dark:bg-slate-900/50">
                          Compliance
                        </td>
                        <td className="px-4 py-2">
                          Addendum to the EU Standard Contractual Clauses (SCCs)
                        </td>
                      </tr>
                    </tbody>
                  </table>
                </div>
              </div>
            </section>

            <section>
              <h2 className="text-2xl font-bold text-slate-900 dark:text-white mb-4">
                How to complain
              </h2>
              <div className="space-y-4">
                <p>
                  If you have any concerns about our use of your personal data,
                  you can make a complaint to us using the contact details at
                  the top of this privacy notice.
                </p>
                <p>
                  If you remain unhappy with how we've used your data, you can
                  also complain to the ICO.
                </p>
                <div className="mt-4 p-6 bg-slate-50 dark:bg-slate-900 rounded-xl border border-slate-100 dark:border-slate-800 text-sm">
                  <p className="font-bold mb-2">
                    Information Commissioner's Office
                  </p>
                  <p>Wycliffe House, Water Lane</p>
                  <p>Wilmslow, Cheshire, SK9 5AF</p>
                  <p className="mt-2">Helpline: 0303 123 1113</p>
                  <p>
                    Website:{" "}
                    <a
                      href="https://www.ico.org.uk/make-a-complaint"
                      className="text-indigo-600 dark:text-indigo-400 hover:underline"
                    >
                      www.ico.org.uk/make-a-complaint
                    </a>
                  </p>
                </div>
              </div>
            </section>
          </div>
        </div>
      </main>

      <Footer />
    </div>
  );
}
